import type { SupportTicketMergeEventData } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDown from "@redotech/redo-icons/chevron-down.svg";
import ChevronUp from "@redotech/redo-icons/chevron-up.svg";
import GitMergeIcon from "@redotech/redo-icons/git-merge_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useUndoSupportTicketMerge } from "../../../hooks/use-undo-support-ticket-merge";
import { getSupportTicketMergePrimaryKey } from "../support-ticket-merge-event-keys";
import { EventTimestamp } from "./event-timestamp";
import * as styles from "./timeline-event.module.css";

function ticketLabel(ticketNumber: number | null): string {
  return ticketNumber === null ? "another ticket" : `ticket #${ticketNumber}`;
}

export const SupportTicketMergeEvent = memo(function SupportTicketMergeEvent({
  event,
  data,
  isCurrentMerge = false,
}: {
  event: SupportTicketTimelineEventResponse;
  data: SupportTicketMergeEventData;
  isCurrentMerge?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const canClose = useHasPermission(SupportPermissions.CLOSE_CONVERSATION);
  const { pendingMergeKey, undoSupportTicketMerge } =
    useUndoSupportTicketMerge();
  const sourceTicket = ticketLabel(data.sourceTicketNumber);
  const destinationTicket = ticketLabel(data.destinationTicketNumber);
  const isDestinationTicket =
    event.supportTicketId === data.destinationSupportTicketId;
  const title =
    event.eventType === "support_ticket_merged"
      ? isDestinationTicket
        ? `${sourceTicket} merged into this ticket`
        : `This ticket was merged into ${destinationTicket}`
      : isDestinationTicket
        ? `${sourceTicket} was unmerged from this ticket`
        : `This ticket was unmerged from ${destinationTicket}`;
  const mergeKey = getSupportTicketMergePrimaryKey(data);
  const canUndo =
    event.eventType === "support_ticket_merged" && isCurrentMerge && canClose;
  const pending = pendingMergeKey === mergeKey;
  const handleToggle = useHandler(() => {
    if (canUndo) {
      setExpanded((current) => !current);
    }
  });
  const handleUndoMerge = useHandler(async () => {
    await undoSupportTicketMerge({
      mergeKey,
      relationshipId: data.relationshipId,
      sourceSupportTicketId: data.sourceSupportTicketId,
      destinationSupportTicketId: data.destinationSupportTicketId,
    });
  });

  return (
    <Flex align="flex-start" className={styles.card} gap="sm">
      <Flex
        align="center"
        className={`${styles.icon} ${styles.iconIndigo}`}
        justify="center"
      >
        <GitMergeIcon />
      </Flex>
      <Flex className={styles.content} dir="column" gap="xxs">
        <Flex
          align="center"
          className={canUndo ? styles.eventActionToggle : undefined}
          justify="space-between"
          onClick={handleToggle}
        >
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {title}
          </Text>
          {canUndo && (
            <Flex align="center" className={styles.chevron}>
              {expanded ? <ChevronUp /> : <ChevronDown />}
            </Flex>
          )}
        </Flex>
        {canUndo && expanded && (
          <Flex className={styles.actionRow}>
            <RedoButton
              disabled={pending}
              hierarchy="tertiary"
              onClick={handleUndoMerge}
              size="xs"
              text="Undo merge"
            />
          </Flex>
        )}
        <EventTimestamp event={event} />
      </Flex>
    </Flex>
  );
});
